import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/ui/DataTable';
import { listTransactions } from '../../services/transactions';

const Transactions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await listTransactions({ page, limit: itemsPerPage, search: searchQuery });
        if (!active) return;
        setRows((res.data || []).map((t) => ({
          id: t._id,
          name: t.customerName || '',
          licenceName: t.licenceName || '',
          type: t.type,
          category: t.category,
          date: new Date(t.date).toLocaleDateString(),
          amount: `${t.currency || 'GBP'} ${Number(t.amount).toFixed(2)}`
        })));
        setTotal(res.pagination?.total || 0);
      } catch (e) {
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, searchQuery]);

  // Define table columns
  const columns = [
    {
      header: 'Transaction #',
      accessor: 'id',
      className: 'text-gray-900 font-medium'
    },
    {
      header: 'Name',
      accessor: 'name',
      className: 'text-gray-900'
    },
    {
      header: 'Licence Name',
      accessor: 'licenceName',
      className: 'text-gray-600'
    },
    {
      header: 'Type',
      accessor: 'type',
      render: (row) => {
        const typeConfig = {
          'Pending': {
            bg: 'bg-blue-50',
            text: 'text-blue-600',
            border: 'border-blue-200',
            dot: 'bg-blue-500'
          },
          'Refund': {
            bg: 'bg-orange-50',
            text: 'text-orange-600',
            border: 'border-orange-200',
            dot: 'bg-orange-500'
          },
          'Received': {
            bg: 'bg-emerald-50',
            text: 'text-emerald-600',
            border: 'border-emerald-200',
            dot: 'bg-emerald-500'
          }
        };

        const config = typeConfig[row.type] || typeConfig['Pending'];

        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
            {row.type}
          </span>
        );
      }
    },
    {
      header: 'Category',
      accessor: 'category',
      className: 'text-gray-900'
    },
    {
      header: 'Date',
      accessor: 'date',
      className: 'text-gray-600'
    },
    {
      header: 'Amount',
      accessor: 'amount',
      className: 'text-gray-900 font-medium'
    },
    {
      header: 'Action',
      accessor: 'action',
      align: 'center',
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M1.5 9C1.5 9 4.5 3 9 3C13.5 3 16.5 9 16.5 9C16.5 9 13.5 15 9 15C4.5 15 1.5 9 1.5 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="9" cy="9" r="2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9.75 2.25H4.5C4.08579 2.25 3.68843 2.41462 3.39462 2.70462C3.10081 2.99462 2.93619 3.38842 2.93619 3.79829V15.7017C2.93619 16.1116 3.10081 16.5054 3.39462 16.7954C3.68843 17.0854 4.08579 17.25 4.5 17.25H13.5C13.9142 17.25 14.3116 17.0854 14.6054 16.7954C14.8992 16.5054 15.0638 16.1116 15.0638 15.7017V7.5L9.75 2.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.75 2.25V7.5H15.0638" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Transactions</h1>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M15.75 7.5H2.25M12 11.25H12.0075M9 11.25H9.0075M6 11.25H6.0075M5.25 15.75H12.75C13.9926 15.75 15 14.7426 15 13.5V4.5C15 3.25736 13.9926 2.25 12.75 2.25H5.25C4.00736 2.25 3 3.25736 3 4.5V13.5C3 14.7426 4.00736 15.75 5.25 15.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Filter
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        data={rows}
        columns={columns}
        searchQuery={searchQuery}
        onSearchChange={(q) => { setPage(1); setSearchQuery(q); }}
        itemsPerPage={itemsPerPage}
        totalItems={total}
        currentPage={page}
        onPageChange={setPage}
        showCheckboxes={false}
        loading={loading}
      />
    </div>
  );
};

export default Transactions;

